import { useGetContractorsByTypeQuery } from "../../apiData/apiDataSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { JSX, useEffect, useRef } from "react";
import { setDossierLoading, updateDossier } from "../../../app/rootReducer";
import ContractorInterface from "../../../common/interfaces/ContractorInterface";
import { useParams } from "react-router-dom";
import FormSelectFuzzy from "../../../common/components/form/FormSelectFuzzy";
import { ContractorNotice } from "./ContractorNotice";
import { handleContractorSelect } from "../utils/handleContractorSelect";
import { ContractorEnum } from "../../../common/constants/ContractorEnum";
import { MissingContractor } from "./MissingContractor";
import { MissingContractorPrompt } from "./MissingContractorPrompt";
import { BovagTermsTypeEnum } from "../../../common/constants/BovagTermsTypeEnum";
import { setVehicleUsage } from "../vehicleInformationSlice";
import { PatchStepsEnum } from "../../../common/constants/PatchStepsEnum";
import { scheduleScrollToStep } from "../../../common/helpers/scrollToStep";

const VehicleInformationInsurance = (): JSX.Element | null => {
    const dispatch = useAppDispatch();

    const { slug } = useParams();
    const { insurance, showContactorNotice, vehicleUsage } = useAppSelector(
        (state) => state.vehicleInformation,
    );
    const { dossier, getParams } = useAppSelector((state) => state.root);
    const { missingContractor } = useAppSelector(
        (state) => state.vehicleInformation,
    );
    const { data: contractors, isLoading } = useGetContractorsByTypeQuery({
        type: "insurance",
        dossierId: dossier?.dossier_id ?? 0,
        preferredEstablishmentId: getParams?.establishmentId,
        preferredEstablishmentChainId: getParams?.establishmentChainId,
    });

    useEffect(() => {
        if (isLoading) {
            dispatch(setDossierLoading(true));
        } else {
            dispatch(setDossierLoading(false));
        }
    }, [isLoading, dispatch]);

    const renderCondition = dossier?.car_brand && dossier?.car_model;
    const vehicleUsageRef = useRef<HTMLDivElement>(null);
    const insuranceContractorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!renderCondition) return;

        scheduleScrollToStep(() =>
            vehicleUsage && contractors
                ? (insuranceContractorRef.current ?? vehicleUsageRef.current)
                : vehicleUsageRef.current,
        );
    }, [
        renderCondition,
        vehicleUsage,
        insurance.activeContractorId,
        contractors,
        missingContractor,
        showContactorNotice,
    ]);

    const fuzzyOptions = {
        keys: [{ name: "label", weight: 1.0 }],
        valueKey: "label",
        maxPatternLength: 8,
        includeScore: true,
        maxResults: 25,
        threshold: 0.6,
    };

    const handleVehicleUsageChange = (usage: BovagTermsTypeEnum): void => {
        dispatch(setVehicleUsage(usage));
        dispatch(
            updateDossier({
                data: { bovag_terms_type: usage },
                method: PatchStepsEnum.PatchBovagTermsType,
            }),
        );
    };

    if (renderCondition) {
        return (
            <>
                <div
                    className="s-appointment__group border-color-primary"
                    ref={vehicleUsageRef}
                >
                    <h1 className="s-appointment__group__title">
                        Is je voertuig zakelijk of privé verzekerd?
                    </h1>
                    <div className={"c-input__group"}>
                        <div className="c-form__buttons c-form__buttons--row c-form__buttons--no-margin">
                            <button
                                className={`c-form__buttons__single border-color-primary border-color-primary-hover ${vehicleUsage === BovagTermsTypeEnum.BUSINESS ? "button-active" : ""}`}
                                type={"button"}
                                onClick={() =>
                                    handleVehicleUsageChange(
                                        BovagTermsTypeEnum.BUSINESS,
                                    )
                                }
                            >
                                <span className="font-face-primary">
                                    Zakelijk
                                </span>
                            </button>
                            <button
                                className={`c-form__buttons__single border-color-primary border-color-primary-hover ${vehicleUsage === BovagTermsTypeEnum.PRIVATE ? "button-active" : ""}`}
                                type={"button"}
                                onClick={() =>
                                    handleVehicleUsageChange(
                                        BovagTermsTypeEnum.PRIVATE,
                                    )
                                }
                            >
                                <span className="font-face-primary">Privé</span>
                            </button>
                        </div>
                        <p>
                            Zo tonen we de juiste BOVAG-voorwaarden voor jouw
                            situatie
                        </p>
                    </div>
                </div>

                {vehicleUsage && (
                    <>
                        <div
                            className="s-appointment__group border-color-primary"
                            ref={insuranceContractorRef}
                        >
                            <h1 className="s-appointment__group__title">
                                Wat is de naam van jouw
                                verzekeringsmaatschappij?
                            </h1>
                            {contractors && (
                                <div className="c-form__select react-select">
                                    <FormSelectFuzzy
                                        fuzzyOptions={fuzzyOptions}
                                        wait={300}
                                        value={
                                            insurance.activeContractorId ?? ""
                                        }
                                        options={contractors.map(
                                            (
                                                newInsurance: ContractorInterface,
                                            ) => {
                                                return {
                                                    value: newInsurance.id,
                                                    label: newInsurance.name,
                                                };
                                            },
                                        )}
                                        onChange={(option) =>
                                            handleContractorSelect({
                                                dispatch,
                                                option,
                                                contractors,
                                                type: ContractorEnum.INSURANCE,
                                            })
                                        }
                                    />
                                    <p style={{ marginTop: "1rem" }}>
                                        Meld de schade ook direct bij de
                                        verzekeringsmaatschappij, zo kunnen we
                                        je sneller van dienst zijn
                                    </p>
                                </div>
                            )}
                        </div>

                        {showContactorNotice && (
                            <ContractorNotice
                                slug={slug}
                                contractors={contractors}
                                activeContractorId={
                                    insurance.activeContractorId ?? null
                                }
                                type={ContractorEnum.INSURANCE}
                            />
                        )}

                        {!insurance.activeContractorId &&
                            !showContactorNotice && <MissingContractorPrompt />}

                        {missingContractor &&
                            !insurance.activeContractorId &&
                            !showContactorNotice && <MissingContractor />}
                    </>
                )}
            </>
        );
    } else return null;
};

export default VehicleInformationInsurance;
